/**
 * COMPOSANT : sellerie-app/frontend/src/pages/Entreprise.tsx
 * Formulaire Entreprise avec gestion des valeurs seedées (fond orange).
 */

import React, { useState, useEffect, useRef } from 'react';
import { entrepriseService, Entreprise as EntrepriseType } from '../services/EntrepriseService';
import { hasSeededCriticalEntreprise } from '../utils/seededEntreprise';

interface EntrepriseData {
  nom: string;
  adresse: string;
  codePostal: string;
  ville: string;
  siret: string;
  telephone: string;
  email: string;
  siteWeb: string;
  logo: string;
  tva: string;
  soumisATVA: boolean;
  tauxTVA: number;
  couleurPrimaire: string;
  couleurSecondaire: string;
  piedPage: string;
  conditionsPaiement: string;
  mentionsLegales: string;
  has_cgv?: boolean;
  cgvFilename?: string;
  cgvSize?: number;
  cgvUploadedAt?: string | null;
}

interface CgvMeta {
  hasCgv: boolean;
  filename: string;
  size: number;
  uploadedAt: string | null;
}

const emptyForm: EntrepriseData = {
  nom: '',
  adresse: '',
  codePostal: '',
  ville: '',
  siret: '',
  telephone: '',
  email: '',
  siteWeb: '',
  logo: '',
  tva: '',
  soumisATVA: true,
  tauxTVA: 20,
  couleurPrimaire: '',
  couleurSecondaire: '',
  piedPage: '',
  conditionsPaiement: '',
  mentionsLegales: '',
  has_cgv: false,
  cgvFilename: '',
  cgvSize: 0,
  cgvUploadedAt: null,
};

const Entreprise: React.FC = () => {
  const TVA_NON_APPLICABLE = 'TVA non applicable, article 293 B du CGI';
  const computeTvaFromSiren = (sirenRaw: string) => {
    const sirenDigits = (sirenRaw || '').replace(/\D/g, '');
    if (sirenDigits.length !== 9) return '';
    const sirenNum = parseInt(sirenDigits, 10);
    const cle = (12 + 3 * (sirenNum % 97)) % 97;
    return `FR ${cle.toString().padStart(2, '0')} ${sirenDigits}`;
  };
  const siretInputRef = useRef<HTMLInputElement | null>(null);
  const cgvInputRef = useRef<HTMLInputElement | null>(null);
  const [formData, setFormData] = useState<EntrepriseData>(emptyForm);
  const [initialFormData, setInitialFormData] = useState<EntrepriseData | null>(null);
  const [seededFields, setSeededFields] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [cgvMeta, setCgvMeta] = useState<CgvMeta>({ hasCgv: false, filename: '', size: 0, uploadedAt: null });
  const [cgvError, setCgvError] = useState<string>('');
  const [cgvLoading, setCgvLoading] = useState<boolean>(false);
  const seededCritical = hasSeededCriticalEntreprise(seededFields, formData);

  const extractCgvMeta = (entrepriseData: EntrepriseType | any): CgvMeta => ({
    hasCgv: !!entrepriseData?.has_cgv,
    filename: entrepriseData?.cgvFilename || '',
    size: entrepriseData?.cgvSize || 0,
    uploadedAt: entrepriseData?.cgvUploadedAt || null,
  });

  const formatSize = (size: number) => {
    if (!size || size <= 0) return '';
    if (size < 1024) return `${size} o`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} Ko`;
    return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
  };

  const mapApiToFormData = (entrepriseData: EntrepriseType | any): EntrepriseData => ({
    nom: entrepriseData?.nom || '',
    adresse: entrepriseData?.adresse || '',
    codePostal: entrepriseData?.codePostal || '',
    ville: entrepriseData?.ville || '',
    siret: entrepriseData?.siret || '',
    telephone: entrepriseData?.telephone || '',
    email: entrepriseData?.email || '',
    siteWeb: entrepriseData?.siteWeb || '',
    logo: entrepriseData?.logo || '',
    tva:
      entrepriseData?.soumisATVA === false || entrepriseData?.soumisTVA === false
        ? TVA_NON_APPLICABLE
        : entrepriseData?.tva || '',
    soumisATVA: entrepriseData?.soumisATVA !== undefined ? entrepriseData.soumisATVA : !!entrepriseData?.tva,
    tauxTVA: entrepriseData?.tauxTVA || 20,
    couleurPrimaire: entrepriseData?.couleurPrimaire || '',
    couleurSecondaire: entrepriseData?.couleurSecondaire || '',
    piedPage: entrepriseData?.piedPage || '',
    conditionsPaiement: entrepriseData?.conditionsPaiement || '',
    mentionsLegales: entrepriseData?.mentionsLegales || '',
    has_cgv: entrepriseData?.has_cgv || false,
    cgvFilename: entrepriseData?.cgvFilename || '',
    cgvSize: entrepriseData?.cgvSize || 0,
    cgvUploadedAt: entrepriseData?.cgvUploadedAt || null,
  });

  const updateDirtyState = (nextData: EntrepriseData) => {
    if (!initialFormData) {
      setIsDirty(false);
      return;
    }
    const hasDiff = Object.keys(nextData).some(
      (key) => (nextData as any)[key] !== (initialFormData as any)[key]
    );
    setIsDirty(hasDiff);
  };

  const isSeeded = (field: string) => seededFields.includes(field);
  const highlightClass = (field: string) => (isSeeded(field) ? 'bg-orange-50 border-orange-300' : '');
  
  const handleConfirmField = async (fieldName: string) => {
    try {
      const response = await entrepriseService.confirmField(fieldName);
      const mapped = mapApiToFormData(response);
      setFormData(mapped);
      setInitialFormData(mapped);
      setSeededFields(response?.seeded_fields || []);
      setCgvMeta(extractCgvMeta(response));
      setSuccessMessage(`Champ "${fieldName}" confirmé.`);
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (error) {
      console.error(`Erreur lors de la confirmation du champ ${fieldName}:`, error);
      setSuccessMessage(`Erreur lors de la confirmation du champ.`);
    }
  };
  
  const seededBadge = (field: string) =>
    isSeeded(field) ? (
      <span className="ml-2 inline-flex items-center gap-1">
        <span className="text-xs font-semibold text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">
          Par défaut
        </span>
        <button
          type="button"
          onClick={() => handleConfirmField(field)}
          className="text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 px-2 py-0.5 rounded-full transition-colors"
          title={`Confirmer la valeur de "${field}"`}
        >
          ✓
        </button>
      </span>
    ) : null;

  useEffect(() => {
    const loadEntrepriseData = async () => {
      try {
        const entrepriseData = await entrepriseService.getEntreprise();
        const mapped = mapApiToFormData(entrepriseData);
        setFormData(mapped);
        setInitialFormData(mapped);
        setSeededFields((entrepriseData as any)?.seeded_fields || []);
        setIsDirty(false);
        setLogoPreview(mapped.logo || null);
        setCgvMeta(extractCgvMeta(entrepriseData));
      } catch (error) {
        console.error("Erreur lors du chargement des données de l'entreprise:", error);
      } finally {
        setLoading(false);
      }
    };

    loadEntrepriseData();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;

    if (type === 'checkbox') {
      const { checked } = e.target as HTMLInputElement;
      const updatedForm = {
        ...formData,
        [name]: checked,
        ...(name === 'soumisATVA' && !checked ? { tva: TVA_NON_APPLICABLE } : {}),
        ...(name === 'soumisATVA' && checked ? { tva: computeTvaFromSiren(formData.siret) || formData.tva } : {}),
      };
      setFormData(updatedForm);
      updateDirtyState(updatedForm);
      return;
    }

    if (type === 'file') {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        const file = files[0];
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Logo = reader.result as string;
          const updatedForm = { ...formData, logo: base64Logo };
          setLogoPreview(base64Logo);
          setFormData(updatedForm);
          updateDirtyState(updatedForm);
        };
        reader.readAsDataURL(file);
      }
      return;
    }

    if (name === 'siteWeb') {
      let urlValue = value;
      if (urlValue && urlValue.trim() !== '' && !urlValue.match(/^https?:\/\//)) {
        urlValue = `https://${urlValue}`;
      }
      const updatedForm = { ...formData, [name]: urlValue };
      setFormData(updatedForm);
      updateDirtyState(updatedForm);
      return;
    }

    const updatedForm = { ...formData, [name]: value };
    setFormData(updatedForm);
    updateDirtyState(updatedForm);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');

    try {
      const changes: Partial<EntrepriseType> = {};
      if (initialFormData) {
        Object.keys(formData).forEach((key) => {
          if ((formData as any)[key] !== (initialFormData as any)[key]) {
            switch (key) {
              case 'nom':
              case 'adresse':
              case 'codePostal':
              case 'ville':
              case 'telephone':
              case 'email':
              case 'siteWeb':
              case 'conditionsPaiement':
              case 'piedPage':
              case 'mentionsLegales':
                (changes as any)[key] = (formData as any)[key];
                break;
              case 'siret':
                changes.siret = formData.siret;
                break;
              case 'logo':
                changes.logo = logoPreview || formData.logo;
                break;
              case 'soumisATVA':
                changes.soumisATVA = formData.soumisATVA;
                // Forcer la valeur TVA côté API lorsqu'on passe en non soumis
                changes.tva = formData.soumisATVA ? formData.tva : TVA_NON_APPLICABLE;
                break;
              case 'tauxTVA':
                changes.tauxTVA = formData.soumisATVA ? formData.tauxTVA : undefined;
                break;
              case 'tva':
                changes.tva = formData.soumisATVA ? formData.tva : TVA_NON_APPLICABLE;
                break;
              case 'couleurPrimaire':
                changes.couleurPrimaire = formData.couleurPrimaire;
                break;
              case 'couleurSecondaire':
                changes.couleurSecondaire = formData.couleurSecondaire;
                break;
              default:
                break;
            }
          }
        });
      }

      // Si rien n'a changé, ne pas envoyer de requête
      if (Object.keys(changes).length === 0) {
        setLoading(false);
        return;
      }

      const savedEntreprise = await entrepriseService.saveEntreprise(changes);
      const mapped = mapApiToFormData(savedEntreprise as any);
      setFormData(mapped);
      setInitialFormData(mapped);
      setSeededFields((savedEntreprise as any)?.seeded_fields || []);
      setCgvMeta(extractCgvMeta(savedEntreprise));
      setIsDirty(false);
      setLogoPreview(mapped.logo || null);

      setSuccessMessage("Informations de l'entreprise enregistrées avec succès !");
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde des données de l'entreprise:", error);
      setSuccessMessage('Erreur lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDefaults = async () => {
    if (isDirty) {
      return;
    }
    setConfirmLoading(true);
    setSuccessMessage('');
    try {
      const updatedEntreprise = await entrepriseService.confirmDefaults();
      const mapped = mapApiToFormData(updatedEntreprise as any);
      setFormData(mapped);
      setInitialFormData(mapped);
      setSeededFields((updatedEntreprise as any)?.seeded_fields || []);
      setIsDirty(false);
      setLogoPreview(mapped.logo || null);
      setCgvMeta(extractCgvMeta(updatedEntreprise));
      setSuccessMessage('Valeurs par défaut confirmées.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Erreur lors de la confirmation des valeurs par défaut:', error);
      setSuccessMessage('Erreur lors de la confirmation. Veuillez réessayer.');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleCgvFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCgvError('');

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setCgvError('Seuls les fichiers PDF sont autorisés.');
      if (cgvInputRef.current) {
        cgvInputRef.current.value = '';
      }
      return;
    }

    setCgvLoading(true);
    try {
      const updated = await entrepriseService.uploadCgv(file);
      setCgvMeta(extractCgvMeta(updated));
      setSuccessMessage('CGV importées avec succès.');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err: any) {
      setCgvError(err?.message || 'Erreur lors du chargement des CGV.');
    } finally {
      setCgvLoading(false);
      if (cgvInputRef.current) {
        cgvInputRef.current.value = '';
      }
    }
  };

  const handleDeleteCgv = async () => {
    setCgvError('');
    setCgvLoading(true);
    try {
      const updated = await entrepriseService.deleteCgv();
      setCgvMeta(extractCgvMeta(updated));
      setSuccessMessage('CGV supprimées.');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err: any) {
      setCgvError(err?.message || 'Erreur lors de la suppression des CGV.');
    } finally {
      setCgvLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des informations de l'entreprise...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {seededCritical && (
        <div className="bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 rounded">
          Ce devis est calculé avec des paramètres par défaut non confirmés.
        </div>
      )}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Informations de l'entreprise</h1>
          <p className="mt-1 text-sm text-gray-500">Ces informations apparaîtront sur vos devis et factures.</p>
        </div>
        <button
          type="button"
          onClick={handleConfirmDefaults}
          disabled={isDirty || confirmLoading}
          title={isDirty ? "Enregistrez d'abord vos modifications" : ''}
          className={`px-4 py-2 rounded-md border text-sm font-medium ${
            isDirty
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
          }`}
        >
          {confirmLoading ? 'Validation...' : '✅ Ces valeurs sont correctes'}
        </button>
      </div>

      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
          {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Informations générales */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Informations générales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="nom" className="form-label flex items-center">
                Nom de l'entreprise
                {seededBadge('nom')}
              </label>
              <input
                type="text"
                id="nom"
                name="nom"
                className={`input-field ${highlightClass('nom')}`}
                value={formData.nom}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="siret" className="form-label flex items-center">
                SIRET
                {seededBadge('siret')}
              </label>
      <input
        type="text"
        id="siret"
        name="siret"
        ref={siretInputRef}
        className={`input-field ${highlightClass('siret')}`}
        value={formData.siret}
        onChange={handleChange}
      />
            </div>
            <div>
              <label htmlFor="adresse" className="form-label flex items-center">
                Adresse
                {seededBadge('adresse')}
              </label>
              <input
                type="text"
                id="adresse"
                name="adresse"
                className={`input-field ${highlightClass('adresse')}`}
                value={formData.adresse}
                onChange={handleChange}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="codePostal" className="form-label flex items-center">
                  Code postal
                  {seededBadge('codePostal')}
                </label>
                <input
                  type="text"
                  id="codePostal"
                  name="codePostal"
                  className={`input-field ${highlightClass('codePostal')}`}
                  value={formData.codePostal}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label htmlFor="ville" className="form-label flex items-center">
                  Ville
                  {seededBadge('ville')}
                </label>
                <input
                  type="text"
                  id="ville"
                  name="ville"
                  className={`input-field ${highlightClass('ville')}`}
                  value={formData.ville}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div>
              <label htmlFor="telephone" className="form-label flex items-center">
                Téléphone
                {seededBadge('telephone')}
              </label>
              <input
                type="tel"
                id="telephone"
                name="telephone"
                className={`input-field ${highlightClass('telephone')}`}
                value={formData.telephone}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="email" className="form-label flex items-center">
                Email
                {seededBadge('email')}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className={`input-field ${highlightClass('email')}`}
                value={formData.email}
                onChange={handleChange}
                placeholder="contact@exemple.fr"
                pattern="^(À compléter|[A-Za-z0-9._%+\\-]+@[A-Za-z0-9.\\-]+\\.[A-Za-z]{2,})$"
                title="Email valide ou 'À compléter'"
              />
            </div>
            <div>
              <label htmlFor="siteWeb" className="form-label flex items-center">
                Site web
                {seededBadge('siteWeb')}
              </label>
              <input
                type="text"
                id="siteWeb"
                name="siteWeb"
                className={`input-field ${highlightClass('siteWeb')}`}
                value={formData.siteWeb.replace(/^https?:\/\//, '')}
                onChange={handleChange}
                placeholder="www.exemple.fr"
              />
              <small className="text-gray-500 mt-1 block">
                Saisissez l'URL sans http:// ou https://, ils seront ajoutés automatiquement
              </small>
            </div>
            <div>
              <label htmlFor="logo" className="form-label flex items-center">
                Logo
                {seededBadge('logo')}
              </label>
              <input
                type="file"
                id="logo"
                name="logo"
                accept="image/*"
                className={`input-field ${highlightClass('logo')}`}
                onChange={handleChange}
              />
              {logoPreview && (
                <div className="mt-2">
                  <img src={logoPreview} alt="Aperçu du logo" className="h-20 object-contain" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Paramètres fiscaux */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Paramètres fiscaux</h2>
          <div className="space-y-4">
            <div className={`flex items-center ${isSeeded('soumisATVA') ? 'bg-orange-50 border border-orange-200 rounded-md px-3 py-2' : ''}`}>
              <input
                type="checkbox"
                id="soumisATVA"
                name="soumisATVA"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={formData.soumisATVA}
                onChange={handleChange}
              />
              <label htmlFor="soumisATVA" className="ml-2 block text-sm text-gray-900 flex items-center">
                Entreprise soumise à TVA
                {seededBadge('soumisATVA')}
              </label>
            </div>
{formData.soumisATVA ? (
  <>
    <div>
      <label htmlFor="tauxTVA" className="form-label flex items-center">
        Taux de TVA applicable (%)
        {seededBadge('tauxTVA')}
      </label>
      <input
        type="number"
        id="tauxTVA"
        name="tauxTVA"
        className={`input-field w-32 ${highlightClass('tauxTVA')}`}
        value={formData.tauxTVA}
        onChange={handleChange}
        min="0"
        max="100"
        step="0.1"
      />
    </div>
    <div>
      <label htmlFor="tva" className="form-label flex items-center">
        N° de TVA intracommunautaire
        {seededBadge('tva')}
      </label>
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <input
            type="text"
            id="tva"
            name="tva"
            className={`input-field flex-1 ${highlightClass('tva')}`}
            value={formData.tva}
            onChange={handleChange}
            placeholder="Ex: FR 12 123456789"
          />
          <button
            type="button"
            className="btn-secondary whitespace-nowrap"
            onClick={() => {
              const autoTva = computeTvaFromSiren(formData.siret);
              if (!autoTva) {
                setSuccessMessage('Renseignez un SIREN valide (9 chiffres) pour calculer le N° de TVA.');
                if (siretInputRef.current) {
                  siretInputRef.current.focus();
                }
                return;
              }
              const updated = { ...formData, tva: autoTva };
              setSuccessMessage('');
              setFormData(updated);
              updateDirtyState(updated);
            }}
          >
            Calculer le N° de TVA
          </button>
        </div>
        <small className="text-gray-500 mt-1 block">
          Calcul automatique basé sur le SIREN. Vérifiez et corrigez si nécessaire.
        </small>
      </div>
    </div>
  </>
) : (
  <div className="text-sm text-gray-700">{TVA_NON_APPLICABLE}</div>
)}
          </div>
        </div>

        {/* CGV (PDF) */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">CGV (PDF)</h2>
          <div className="space-y-4">
            {cgvMeta.hasCgv ? (
              <div className="text-sm text-gray-700">
                <p className="font-medium">
                  <a
                    href="/api/entreprise/cgv/download"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    {cgvMeta.filename || 'CGV.pdf'}
                  </a>
                </p>
                <p className="text-gray-500">
                  {formatSize(cgvMeta.size) || 'Taille inconnue'}
                  {cgvMeta.uploadedAt ? ` • Ajouté le ${new Date(cgvMeta.uploadedAt).toLocaleDateString()}` : ''}
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Aucun PDF de CGV configuré pour le moment.</p>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => cgvInputRef.current?.click()}
                disabled={cgvLoading}
              >
                {cgvMeta.hasCgv ? 'Remplacer le PDF' : 'Ajouter un PDF'}
              </button>
              {cgvMeta.hasCgv && (
                <button
                  type="button"
                  className="btn-secondary text-red-700 border-red-200 hover:bg-red-50"
                  onClick={handleDeleteCgv}
                  disabled={cgvLoading}
                >
                  Supprimer
                </button>
              )}
              {cgvLoading && <span className="text-sm text-gray-500">Traitement en cours...</span>}
            </div>
            <input
              ref={cgvInputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={handleCgvFileChange}
            />
            {cgvError && <p className="text-sm text-red-600">{cgvError}</p>}
          </div>
        </div>

        {/* Personnalisation des devis */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Personnalisation des devis</h2>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="couleurPrimaire" className="form-label">
                  Couleur primaire
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    id="couleurPrimaire"
                    name="couleurPrimaire"
                    className="h-10 w-20 rounded border-gray-300"
                    value={formData.couleurPrimaire || '#3B82F6'}
                    onChange={handleChange}
                  />
                  <input
                    type="text"
                    className="input-field flex-1"
                    name="couleurPrimaire"
                    value={formData.couleurPrimaire || '#3B82F6'}
                    onChange={handleChange}
                    placeholder="#3B82F6"
                  />
                </div>
                <small className="text-gray-500 mt-1 block">Utilisée pour les titres et éléments principaux</small>
              </div>
              <div>
                <label htmlFor="couleurSecondaire" className="form-label">
                  Couleur secondaire
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    id="couleurSecondaire"
                    name="couleurSecondaire"
                    className="h-10 w-20 rounded border-gray-300"
                    value={formData.couleurSecondaire || '#6B7280'}
                    onChange={handleChange}
                  />
                  <input
                    type="text"
                    className="input-field flex-1"
                    name="couleurSecondaire"
                    value={formData.couleurSecondaire || '#6B7280'}
                    onChange={handleChange}
                    placeholder="#6B7280"
                  />
                </div>
                <small className="text-gray-500 mt-1 block">Utilisée pour les sous-titres et textes secondaires</small>
              </div>
            </div>
            <div>
              <label htmlFor="conditionsPaiement" className="form-label flex items-center">
                Conditions de paiement par défaut
                {seededBadge('conditionsPaiement')}
              </label>
              <textarea
                id="conditionsPaiement"
                name="conditionsPaiement"
                rows={3}
                className={`input-field ${highlightClass('conditionsPaiement')}`}
                value={formData.conditionsPaiement}
                onChange={handleChange}
                placeholder="Ex: Paiement à 30 jours fin de mois"
              />
              <small className="text-gray-500 mt-1 block">Ces conditions apparaîtront sur tous vos devis</small>
            </div>
            <div>
              <label htmlFor="piedPage" className="form-label flex items-center">
                Pied de page personnalisé
                {seededBadge('piedPage')}
              </label>
              <textarea
                id="piedPage"
                name="piedPage"
                rows={2}
                className={`input-field ${highlightClass('piedPage')}`}
                value={formData.piedPage}
                onChange={handleChange}
                placeholder="Ex: Merci de votre confiance"
              />
              <small className="text-gray-500 mt-1 block">Texte affiché en bas de chaque page de devis</small>
            </div>
            <div>
              <label htmlFor="mentionsLegales" className="form-label flex items-center">
                Mentions légales
                {seededBadge('mentionsLegales')}
              </label>
              <textarea
                id="mentionsLegales"
                name="mentionsLegales"
                rows={4}
                className={`input-field ${highlightClass('mentionsLegales')}`}
                value={formData.mentionsLegales}
                onChange={handleChange}
                placeholder="Ex: RCS, Capital social, TVA intracommunautaire..."
              />
              <small className="text-gray-500 mt-1 block">
                Informations légales obligatoires affichées sur vos documents
              </small>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button type="submit" className="btn-primary">
            Enregistrer les modifications
          </button>
        </div>
      </form>
    </div>
  );
};

export default Entreprise;
